// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { memoizeFunction } from "@fluentui/react";

import { getRibbonTextContainerSelector } from "@1js/acui-common";
import type { AppContextType } from "@1js/acui-context";
import type {
  MenuDefinition,
  MenuSectionGeneric,
  SimpleMenuItemControlProps,
} from "@1js/acui-menu";

import {
  addInitialScalingStepsStyles,
  addMaxWidthSelectors,
  clearScaledGalleryCaches,
  estimateRibbonScalingStepsSLR,
  getClassNameCache,
  getControlGroupFromScalingStep,
  getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector,
  getCoreRibbonElementForOverflowSelector,
  getCoreRibbonElementSelector,
  getDropPreviewItemFromGalleryIndexSelector,
  getGalleriesConvertedToFlyoutByIdMode,
  getGhostFlyoutVisibleSelector,
  getMoveToOverflowScalingClassNameForScalingIndex,
  getMoveToOverflowSelector,
  getRibbonBottomBarWidthSLR,
  getSelectorsForScalingSteps,
  initializeVirtualScalingStateSLR,
  setGalleriesConvertedToFlyoutByIdMode,
} from ".";
import type {
  BuildTimeControlWidths,
  EstimatedScalingWidthDict,
  RibbonScalingStateSLRWithCacheKeys,
  ScaledRibbonGroupSLR,
  ScalingClassNameProperties,
  ScalingStepsStyle,
  Selectors,
  WithSectionId,
} from "..";
import {
  hiddenControlStyle,
  makeControlVisibleStyle,
  makeMeasurableControlVisibleStyle,
  setEstimatedScalingWidths,
  unpinComboboxStyle,
} from "..";
import type {
  MoveToOverflow,
  RibbonControlScalingTransformation,
  RibbonGroupScalingTransformation,
  RibbonScalingStep,
  RibbonScalingTransformation,
} from "../../../Scaling";
import {
  singleMoveToOverflowScalingClassNameIndex,
  singleRibbonOverflowMenuId,
} from "../../../Scaling";
import type { MoveGroupToSingleOverflow } from "../../../Scaling/MoveGroupToSingleOverflow";
import type { IStyle } from "@fluentui/style-utilities";
import {
  chevronWidth,
  getVerticalDividerWidthSLR,
  tabContentContainerPadding,
} from "../RibbonBottomBarConstants";
import { vNextRibbonModeSwitcherLeftRightMargin } from "../../../ribbonStylingConstantsBase";

export let estimatedWidths: EstimatedScalingWidthDict = {};
let lastKnownScalingState: RibbonScalingStateSLRWithCacheKeys | undefined =
  undefined;
let lastKnownWidths: BuildTimeControlWidths | undefined = undefined;
let lastKnownMergedClassName: string | undefined = undefined;
let controlGroupsWithOverflowingContent: string[] = [];

/**
 * Gets a className that corresponds to the given scaling state
 * @param scalingProperties Properties used to compute the className
 * @returns A className for the given scaling state
 */
export function getScalingClassNameSLR(
  scalingProperties: ScalingClassNameProperties<RibbonScalingStateSLRWithCacheKeys>,
  mergeStyles: (...args: IStyle[]) => string // Passing this in as a param so this module doesn't need to take a hard dependency on fluent
) {
  const { scalingState, knownWidths, forceRecalc } = scalingProperties;

  // If we are not forced to recalc, we have known mergedClassName (we've calculated something before),
  // and our known widths are the same; we can potentially return a cached className
  if (
    !forceRecalc &&
    lastKnownMergedClassName &&
    JSON.stringify(knownWidths) === JSON.stringify(lastKnownWidths)
  ) {
    // If we have the previous scaling state and the cache keys are the same, return the
    // previously computed className
    if (
      lastKnownScalingState &&
      lastKnownScalingState.cacheKey === scalingState.cacheKey
    ) {
      return lastKnownMergedClassName;
    }

    // The scaling state is different from the previous call, but we may have already
    // computed the className and stored it in the cache. if so, return the cached className
    const cachedEntry = getClassNameCache().getCachedEntry(scalingState);
    if (cachedEntry) {
      lastKnownScalingState = scalingState;
      lastKnownMergedClassName = cachedEntry.className;
      estimatedWidths = cachedEntry.estimatedWidths;
      return lastKnownMergedClassName;
    }
  }

  const mergedStylesClassName = mergeStyles(
    getScalingStylesSLR(scalingProperties)
  );
  getClassNameCache().addMeasurementToCache(
    scalingState,
    mergedStylesClassName,
    estimatedWidths
  );
  lastKnownMergedClassName = mergedStylesClassName;

  return mergedStylesClassName;
}

/**
 * Gets a className that corresponds to the given scaling state
 * @param scalingProperties Properties used to compute the className
 * @returns A className for the given scaling state
 */
export function getScalingStylesSLR(
  scalingProperties: ScalingClassNameProperties<RibbonScalingStateSLRWithCacheKeys>
) {
  const {
    scalingState,
    knownWidths,
    reservedSpace,
    reactMLRIsEnabled,
    ensureFullWidths,
    initialRender,
    appContext,
    modifiedEstimatedWidths,
    setGlobalEstimatedWidths,
  } = scalingProperties;

  // We have not computed the styling for this scaling state,
  // set up variables to build new styling
  lastKnownScalingState = scalingState;
  const style: ScalingStepsStyle = { selectors: {} };
  controlGroupsWithOverflowingContent = [];
  clearScaledGalleryCaches("SingleLine");

  // Add the styles for the initial control scaling steps
  addInitialScalingStepsStyles(
    scalingState,
    style,
    getScalingStepsStylesBase,
    appContext
  );

  // Add the initial overflow styles
  addInitialOverflowStyles(scalingState, style);

  // We only need to run the following steps if we are not in the initial render
  if (!initialRender) {
    if (modifiedEstimatedWidths) {
      estimatedWidths = modifiedEstimatedWidths;
    } else {
      // Update the estimated width of the ribbon for each scaling step
      updateEstimatedRibbonScalingStepWidths(
        scalingState,
        knownWidths,
        reservedSpace,
        !!reactMLRIsEnabled,
        !!ensureFullWidths,
        appContext
      );
    }

    // Add the styles for the scaling steps. This will add the styles for the non-initial scaling steps
    addScalingStepsStyles(scalingState, style, appContext);

    if (setGlobalEstimatedWidths) {
      setEstimatedScalingWidths(estimatedWidths);
    }
  }
  return style;
}

/**
 * Adds the styles for scaling the ribbon controls for the scaling state
 * based off of the estimated widths
 * @param scalingState The scaling state to build the styles for
 * @param style The style object to write the new scaling styles
 */
const addScalingStepsStyles = (
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  style: ScalingStepsStyle,
  appContext: AppContextType
) => {
  // Loop over the estimated widths to get the max-widths.
  // The first value should be the initial width (after the initialScalingSteps applied)
  for (const key in estimatedWidths) {
    const scalingIndexFromWidth = +key;

    // Add the selectors that make responsive scaling work
    addMaxWidthSelectors(
      style,
      estimatedWidths[key],
      scalingState,
      scalingIndexFromWidth,
      getScalingStepsStylesBase,
      appContext
    );
  }
};

/**
 * Base function to build the styles for the given scaling state
 * @param scalingStepIndex The top level scaling state index
 * @param scalingState The scaling state that should be processed
 * @returns a styles object for (selectors and style attributes) for the scaling step (the scaling step from the given scaling state at the given index)
 */
const getScalingStepsStylesBase = (
  scalingStepIndex: number,
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  appContext: AppContextType
) => {
  const selectors: Selectors = {};
  const scalingStep: RibbonScalingTransformation = scalingState.scalingSteps[
    scalingStepIndex
  ] as RibbonScalingTransformation;

  // If we don't get a scalingStep we cannot calculate the correct selectors, return early
  if (!scalingStep) {
    return selectors;
  }

  // Get core selector for the scaling step
  const selector: string = getCoreScalingStepSelectorBase(
    scalingStep,
    appContext,
    scalingStepIndex
  );

  // Get the unpin combobox selector, if needed
  const unpinComboboxSelector = getUnpinComboboxSelector(
    scalingStep,
    scalingState
  );

  // Get additional selectors for gallery to flyout, if needed
  const makeGhostFlyoutVisibleSelector =
    getGhostFlyoutVisibleSelector(scalingStep);

  // Add overflow back if applicable
  const makeOverflowVisibleSelectors = getOverflowVisibleSelectors(
    scalingState,
    scalingStepIndex
  );

  if (selector) {
    selectors[selector] = hiddenControlStyle;
  }

  if (makeGhostFlyoutVisibleSelector) {
    selectors[makeGhostFlyoutVisibleSelector] =
      makeMeasurableControlVisibleStyle;
  }

  if (makeOverflowVisibleSelectors) {
    selectors[makeOverflowVisibleSelectors] = makeControlVisibleStyle;
  }

  if (unpinComboboxSelector) {
    selectors[unpinComboboxSelector] = unpinComboboxStyle;
  }

  return selectors;
};

/**
 * This function builds up the selectors to make the overflows visible at the right time based on the scaling state
 * @param scalingState The scaling state
 * @param scalingStepIndex The scaling step index that should be processed
 * @param controlGroupsWithOverflowingContent An array to get and update the groups with overflowing content
 * @returns Selectors for when each overflow should be visible for the given scaling step index in the given scaling state
 */
const getOverflowVisibleSelectors = (
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  scalingStepIndex: number
) => {
  // Add overflow back if applicable
  let makeOverflowVisibleSelectors = "";

  // Make the current scaling step an array to align types for easier processing
  const currentScalingSteps: RibbonScalingTransformation[] =
    getScalingStepsAtIndexAsArray(scalingStepIndex, scalingState.scalingSteps);

  // Keep track of when we've set the single overflow to be visible
  let haveMadeSingleOverflowVisible = false;

  // Make overflow visible, if applicable
  for (const scalingStep of currentScalingSteps) {
    // If we are processing a moveToOverflow or moveGroupToOverflow, we may need to make the overflow visible if we have not already
    if (stepContainsToOverflow(scalingStep)) {
      // Get the control group for the scaling step
      const controlGroup = getControlGroupFromScalingStep(
        scalingState,
        scalingStep
      );

      // Check if this is the first moveToOverflow for this group, add the a selector to make the overflow visible
      if (
        controlGroup &&
        addGroupWithOverflowIfFirstInstance(controlGroup.id)
      ) {
        const existingVisibleSelectors = makeOverflowVisibleSelectors
          ? `${makeOverflowVisibleSelectors}, `
          : "";

        // If we are dealing with a single overflow and are processing the first MoveToOverflow for this group,
        // make the single overflow visible, if needed, otherwise set make the group's overflow visible
        if (
          !controlGroup.hasOwnOverflow &&
          scalingState.scaledEndOfRibbonOverflowMenu
        ) {
          // If this is our first MoveToOverflow across groups, make the single overflow visible
          if (!haveMadeSingleOverflowVisible) {
            haveMadeSingleOverflowVisible = true;
            makeOverflowVisibleSelectors = `${existingVisibleSelectors}${getCoreRibbonElementForOverflowSelector(
              singleRibbonOverflowMenuId
            )}, ${getMoveToOverflowSelector(
              singleMoveToOverflowScalingClassNameIndex
            )}`;
          }
        } else {
          // We get here when we are processing the first moveToOverflow for a group (where the overflow is not going to a single overflow).
          // Make the group's overflow visible
          makeOverflowVisibleSelectors = `${existingVisibleSelectors}${getCoreRibbonElementForOverflowSelector(
            controlGroup.id
          )}`;
        }
      }
    }
  }

  return makeOverflowVisibleSelectors;
};

/**
 * Returns a selector based on the scaling index, the scaling steps, and the overall scaling index
 * @param scalingSteps The scaling step to build the selector from
 * @param topLevelScalingIndex The index in the top level set of scaling steps, used for indexing overflow selectors
 * @returns a selector for the given scaling step. This determines control, label, and overflow visibility
 */
const getCoreScalingStepSelector = (
  scalingStep: Readonly<RibbonScalingStep>,
  appContext: AppContextType,
  topLevelScalingIndex: number
): string => {
  // Get the moveToOverflow selector for the current top level scaling index.
  // The vertical dividers also set the className this selector uses, when it needs to be dropped
  const moveToOverflowFullClassNameSelector =
    getMoveToOverflowSelector(topLevelScalingIndex);

  if (Array.isArray(scalingStep)) {
    // Build a selector comprised of all scaling steps in the array since they will all be scaled at the same time
    return getSelectorsForScalingSteps(
      scalingStep,
      getCoreScalingStepSelectorBase,
      appContext,
      topLevelScalingIndex,
      moveToOverflowFullClassNameSelector
    );
  }

  // Check if we are a control scaling transformation
  if (!Object(scalingStep).hasOwnProperty("controlId")) {
    // If we are moving group to single overflow, we need to use the groupId
    if (
      (scalingStep as RibbonGroupScalingTransformation).type ===
      "MoveGroupToSingleOverflow"
    ) {
      return getCoreRibbonElementForOverflowSelector(
        (scalingStep as RibbonGroupScalingTransformation).groupId
      );
    }

    // We do not know what type of scaling transformation this is, return an empty string
    return "";
  }

  return getSelectorForScalingStepBasedOnType(
    scalingStep,
    moveToOverflowFullClassNameSelector
  );
};

/**
 * Memoized - Returns a selector based on the scaling index, the scaling steps, and the overall scaling index
 * @param scalingSteps The scaling step to build the selector from
 * @param topLevelScalingIndex The index in the top level set of scaling steps, used for indexing overflow selectors
 * @returns a selector for the given scaling step. This determines control, label, and overflow visibility
 */
const getCoreScalingStepSelectorMemo = memoizeFunction(
  (
    scalingStep: Readonly<RibbonScalingStep>,
    appContext: AppContextType,
    topLevelScalingIndex: number
  ): string => {
    return getCoreScalingStepSelector(
      scalingStep,
      appContext,
      topLevelScalingIndex
    );
  }
);

/**
 * Returns a selector based on the scaling index, the scaling steps, and the overall scaling index
 * @param scalingSteps The scaling step to build the selector from
 * @param topLevelScalingIndex The index in the top level set of scaling steps, used for indexing overflow selectors
 * @returns a selector for the given scaling step. This determines control, label, and overflow visibility
 */
const getCoreScalingStepSelectorBase = (
  scalingStep: Readonly<RibbonScalingStep> | undefined,
  appContext: AppContextType,
  topLevelScalingIndex: number
): string => {
  if (!scalingStep) {
    return "";
  }

  // Do not memoize the DropPreviewItemFromGallery scaling step due to intermittent scaling issues
  const scalingStepSelectorFn =
    (scalingStep as RibbonScalingTransformation).type ===
    "DropPreviewItemFromGallery"
      ? getCoreScalingStepSelector
      : getCoreScalingStepSelectorMemo;

  return scalingStepSelectorFn(scalingStep, appContext, topLevelScalingIndex);
};

/**
 * Gets a selector to unpin the next combobox if needed
 * @param scalingStep The scaling step used to build the selector
 * @param scalingState The scaling state used to get controlGroup, control types, and order in the group
 * @returns A selector that matches the next combobox if it needs to be unpinned, otherwise an empty string
 */
function getUnpinComboboxSelector(
  scalingStep: RibbonScalingTransformation,
  scalingState: RibbonScalingStateSLRWithCacheKeys
): string {
  // If this is not a MoveToOverflow step, there is nothing to be done
  if (scalingStep.type !== "MoveToOverflow") {
    return "";
  }
  const controlGroup = getControlGroupFromScalingStep(
    scalingState,
    scalingStep
  );

  // If we didn't find the controlGroup or we do not have top level controls, we are done
  if (
    !controlGroup ||
    !controlGroup.topLevelControls ||
    controlGroup.topLevelControls.length === 0
  ) {
    return "";
  }

  const controls = controlGroup.topLevelControls;
  const controlToScaleIndex = controls.findIndex(
    (control) => control.id === scalingStep.controlId
  );

  // If we found the control, it is not last control, and is a combobox;
  // we may need to unpin the next combobox
  if (
    controlToScaleIndex >= 0 &&
    controlToScaleIndex + 1 < controls.length &&
    controls[controlToScaleIndex].type === "AppComboBoxProps"
  ) {
    const nextControl = controls[controlToScaleIndex + 1];
    // If the next control is a combobox and it has pinToPreviousCombobox set to true,
    // we need to return the selector for the next control
    if (
      nextControl.type === "AppComboBoxProps" &&
      nextControl.pinToPreviousComboBox
    ) {
      return getCoreRibbonElementSelector(
        nextControl.id,
        undefined /* suffix */
      );
    }
  }

  return "";
}

/**
 * Gets a selector for the element in the given scaling step based on transformation type
 * @param scalingStep The scaling step used to build the selector
 * @param moveToOverflowFullClassNameSelector The MoveToOverflow selector for this scaling step
 * @returns A selector for the given scaling step or an empty string if do not know the scaling transformation
 */
function getSelectorForScalingStepBasedOnType(
  scalingStep: Readonly<RibbonScalingStep>,
  moveToOverflowFullClassNameSelector: string
) {
  const controlId = (scalingStep as RibbonControlScalingTransformation)
    .controlId;

  const elementSelector = getGalleriesConvertedToFlyoutByIdMode(
    controlId,
    "SingleLine"
  )
    ? getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector(
        controlId
      )
    : getCoreRibbonElementSelector(controlId, undefined /* suffix */);

  // Build the selector for the given controlId for the give scaling step
  switch ((scalingStep as RibbonScalingTransformation).type) {
    case "MoveToOverflow":
      return `${elementSelector}, ${moveToOverflowFullClassNameSelector}`;
    case "DropLabel":
      return `${elementSelector} ${getRibbonTextContainerSelector(
        true /*useOptimizedSelectors*/
      )}`;
    case "DropPreviewItemFromGallery":
      return `${elementSelector} ${getDropPreviewItemFromGalleryIndexSelector(
        controlId,
        "SingleLine"
      )}`;
    case "GalleryToFlyout":
      setGalleriesConvertedToFlyoutByIdMode(
        controlId,
        "SingleLine",
        true /* isCovertedToFlyout */
      );
      return elementSelector;
    case "MoveGroupToSingleOverflow":
      return getCoreRibbonElementForOverflowSelector(
        (scalingStep as RibbonGroupScalingTransformation).groupId
      );
    default:
      return "";
  }
}

/**
 * Add the initial overflow styles for both groups and the single overflow
 * @param scalingState The scaling state used to calculate the initial overflow styles
 * @param style The styled object where the initial overflow styles will be added
 * @param controlGroupsWithOverflowingContent An array to get and update the groups with overflowing content
 */
function addInitialOverflowStyles(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  style: ScalingStepsStyle
) {
  const allGroupsHaveOwnOverflow = addInitialGroupOverflowStyles(
    scalingState,
    style
  );
  addInitialSingleOverflowStyle(scalingState, allGroupsHaveOwnOverflow, style);
}

function addInitialGroupOverflowStyles(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  style: ScalingStepsStyle
) {
  let allGroupsHaveOwnOverflow = true;
  scalingState.scaledRibbonGroups.forEach((group) => {
    allGroupsHaveOwnOverflow =
      allGroupsHaveOwnOverflow && !!group.hasOwnOverflow;

    // Get the actual set of initial controls that are in the overflow.
    // This is required since our scaling state contains both no scaling steps
    // and all scaling steps applied for responsive rendering.
    const initialOverflowControlsCount = group.overflowControls
      ? group.overflowControls.filter(
          (overflowControl) =>
            !group.topLevelControls.find(
              (topLevelControl) => topLevelControl.id === overflowControl.id
            )
        ).length
      : 0;

    // Make the overflow hidden if we have no initial overflow items or
    // the group does not have its own overflow
    if (initialOverflowControlsCount === 0 || group.hasOwnOverflow === false) {
      style.selectors[getCoreRibbonElementForOverflowSelector(group.id)] =
        hiddenControlStyle;
    }

    if (initialOverflowControlsCount > 0) {
      addGroupWithOverflowIfFirstInstance(group.id);
    }
  });
  return allGroupsHaveOwnOverflow;
}

/**
 * Adds the initial overflow styles to the style object for the given scaling state, if applicable
 * @param scalingState The scaling state to build the initial single overflow control styles
 * @param allGroupsHaveOwnOverflow Do all the groups of the scaling state have their own overflow?
 * @param style The style object to add the single overflow styles, if applicable
 * @returns
 */
function addInitialSingleOverflowStyle(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  allGroupsHaveOwnOverflow: boolean,
  style: ScalingStepsStyle
) {
  // If all groups have their own overflow or we do not have a single overflow menu,
  // we do not need to set a style for the initial overflow since there will never be a single overflow
  if (allGroupsHaveOwnOverflow || !scalingState.scaledEndOfRibbonOverflowMenu) {
    return;
  }

  // Get the index the the single overflow becomes visible
  const indexWhenSingleOverflowBecomesVisible =
    scalingState.scalingSteps.findIndex((scalingStep) =>
      stepContainsToSingleOverflow(
        scalingState,
        scalingStep as RibbonScalingTransformation
      )
    );

  // If the single overflow is initially visible, we do not need to hide it, return early
  if (
    indexWhenSingleOverflowBecomesVisible >= 0 &&
    indexWhenSingleOverflowBecomesVisible <
      scalingState.minimumScalingStepsToApply
  ) {
    return;
  }

  // If we get here the single overflow is not visible initially,
  // make sure it is not visible
  style.selectors[
    `${getCoreRibbonElementForOverflowSelector(
      singleRibbonOverflowMenuId
    )}, ${getMoveToOverflowSelector(singleMoveToOverflowScalingClassNameIndex)}`
  ] = hiddenControlStyle;
}

/**
 * Update the estimated ribbon widths based on the provided scaling steps. This function also
 * adds new control, previously unknown, widths to known widths
 * @param scalingState The scaling state to used to calculate the estimated ribbon widths per scaling step
 * @param knownWidths The known control widths
 * @param reservedSpace How much space is reserved for non-Ribbon elements
 * @param reactMLRIsEnabled Is the react mlr enabled
 * @param ensureFullWidths Should we make sure we have all widths measured (including textContainer)?
 */
function updateEstimatedRibbonScalingStepWidths(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  knownWidths: BuildTimeControlWidths,
  reservedSpace: number,
  reactMLRIsEnabled: boolean,
  ensureFullWidths: boolean,
  appContext: AppContextType
) {
  const scalingData = getRibbonBottomBarWidthSLR(
    scalingState,
    knownWidths,
    // account for bottomBar padding, the modeSwitcher, and add a vertical divider width
    // buffer(without the buffer the last divider could be cut off intermittently)
    // Next styles adds 4px of margin on each side of ribbon mode button
    tabContentContainerPadding +
      (reactMLRIsEnabled ? chevronWidth : 0) +
      (reactMLRIsEnabled ? vNextRibbonModeSwitcherLeftRightMargin * 2 : 0) +
      getVerticalDividerWidthSLR() +
      reservedSpace,
    reactMLRIsEnabled,
    ensureFullWidths
  );

  // Initialize the virtualScalingState and fill it with our known widths
  const draftVirtualScalingState = initializeVirtualScalingStateSLR(
    scalingState,
    knownWidths
  );
  lastKnownWidths = { ...knownWidths };

  // Determine the effect of applying each scaling step
  estimatedWidths = estimateRibbonScalingStepsSLR(
    scalingState,
    scalingData.width,
    appContext,
    draftVirtualScalingState
  );
}

/**
 * Add the provided groupId to controlGroupsWIthOverflowingContent if it is the first instance
 * of a control in this group overflowing
 * @param groupId The group id where an overflow is happening
 * @returns True this was the first instance of a control overflowing in the given group
 */
const addGroupWithOverflowIfFirstInstance = (groupId: string) => {
  if (controlGroupsWithOverflowingContent.includes(groupId)) {
    return false;
  }
  controlGroupsWithOverflowingContent.push(groupId);
  return true;
};

/**
 * Checks if a scaling step contains a *ToOverflow step
 * @param scalingStep The scaling step to check for a moveToOverflow step
 * @returns True if a *ToOverflow step was found, false otherwise
 */
export function stepContainsToOverflow(
  scalingStep:
    | RibbonScalingTransformation
    | RibbonScalingTransformation[]
    | undefined
): boolean {
  if (Array.isArray(scalingStep)) {
    return scalingStep.some((step) => stepContainsToOverflow(step));
  }

  return (
    !!scalingStep &&
    (scalingStep.type === "MoveToOverflow" ||
      scalingStep.type === "MoveGroupToSingleOverflow")
  );
}

/**
 * Checks if a scaling step contains a *ToOverflow step that moves to a single overflow
 * @param scalingState The full scaling state
 * @param scalingStep The scaling step to check for a moveToOverflow step that moves to a single overflow
 * @returns True if a *ToOverflow step that moves to a single overflow was found, false otherwise
 */
function stepContainsToSingleOverflow(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  scalingStep:
    | RibbonScalingTransformation
    | RibbonScalingTransformation[]
    | undefined
): boolean {
  if (!scalingStep) {
    return false;
  }

  if (Array.isArray(scalingStep)) {
    return scalingStep.some((step) =>
      stepContainsToSingleOverflow(scalingState, step)
    );
  }

  // Return true if the whole group is moving to a single overflow or
  // if the control is moving to overflow and the control group does not have its own overflow
  return (
    scalingStep.type === "MoveGroupToSingleOverflow" ||
    (scalingStep.type === "MoveToOverflow" &&
      !getControlGroupFromScalingStep(scalingState, scalingStep)
        ?.hasOwnOverflow)
  );
}

/**
 * Gets all the *ToOverflow scaling steps inside of the given scaling step
 * @param scalingStep The scaling step to get all *toOverflow scaling steps
 * @returns An array of all *ToOverflow scaling steps
 */
export function getToOverflowSteps(
  scalingStep:
    | RibbonScalingTransformation
    | RibbonScalingTransformation[]
    | undefined
): RibbonScalingTransformation[] {
  let result: RibbonScalingTransformation[] = [];
  if (Array.isArray(scalingStep)) {
    scalingStep.forEach(
      (step) => (result = result.concat(getToOverflowSteps(step)))
    );
    return result;
  }

  return !!scalingStep && stepContainsToOverflow(scalingStep)
    ? [scalingStep]
    : [];
}

/**
 * Returns an array for the scaling step at the given scaling index
 * @param scalingIndex The scaling index to access in the scaling step
 * @param scalingSteps The scaling steps
 * @returns an array consisting of the scaling step at the given scaling index
 */
function getScalingStepsAtIndexAsArray(
  scalingIndex: number,
  scalingSteps: Readonly<RibbonScalingStep>[]
): RibbonScalingTransformation[] {
  const step = scalingSteps[scalingIndex];
  return Array.isArray(step) ? step : [step as RibbonScalingTransformation];
}

/**
 * This function will populate the overflow for the given groupId
 * with the correct MenuDefinition once clicked
 * @param groupId The groupId to build the overflow menu definition for
 * @returns A menu definition based off of the last know scaling state and window width,
 * or undefined if the menu definition cannot be determined
 */
export function getCurrentOverflowMenuDefinition(
  groupId: string,
  overflowMenuDefinition?: MenuDefinition
): MenuDefinition | undefined {
  if (!lastKnownScalingState || !overflowMenuDefinition) {
    return undefined;
  }

  let scalingStepToApply =
    lastKnownScalingState.minimumScalingStepsToApply || 0;

  // Note: This function is used only by office-online-ux, and therefore we don't worry about "reserved space"
  // Follow on bug: https://office.visualstudio.com/OC/_workitems/edit/6006990/
  const spaceAvailable = window.innerWidth;

  // If the full contents do not fit, find the right scaling step
  if (
    !estimatedWidths[scalingStepToApply] ||
    spaceAvailable < estimatedWidths[scalingStepToApply]
  ) {
    let step = undefined;
    for (step in estimatedWidths) {
      if (estimatedWidths[step] < spaceAvailable) {
        scalingStepToApply = +step;
        break;
      }
    }

    // If we get here and we haven't changed stepsToApply, we walked off the end of the scaling steps
    // and the space available is smaller than all the scaling steps, choose the last one
    if (
      step &&
      (scalingStepToApply ===
        lastKnownScalingState.minimumScalingStepsToApply ||
        0)
    ) {
      scalingStepToApply = lastKnownScalingState.scalingSteps.length;
    }
  }

  const allOverflowControlIds: string[] = [];

  // Compile all of the controls that are in overflows up to the given scaling step
  buildOverflowControlIdArray(
    lastKnownScalingState.scalingSteps.slice(0, scalingStepToApply),
    allOverflowControlIds
  );
  const shouldRenderSingleOverflowMenu =
    !!lastKnownScalingState.scaledEndOfRibbonOverflowMenu;

  // Check if we are dealing with the single overflow button
  if (groupId === singleRibbonOverflowMenuId) {
    // If we are told to get a single overflow definition but should not
    // be rendering a single overflow menu, shouldRenderSingleOverflowMenu wins
    if (!shouldRenderSingleOverflowMenu) {
      return undefined;
    }

    return getSingleOverflowMenuDefinition(
      overflowMenuDefinition,
      allOverflowControlIds
    );
  }

  // If we get here, we are a typical group overflow (not the single overflow).
  // Attempt to find the group
  const group = lastKnownScalingState.ribbonGroupDefinitions.groups.find(
    (group) => group.id === groupId
  );

  // If we were not able to find the group or it should not have an overflow, return
  if (!group || (!group.hasOwnOverflow && shouldRenderSingleOverflowMenu)) {
    return undefined;
  }

  // Attempt to find the section for this control group in the menu definition
  const section = overflowMenuDefinition.sections.find(
    (section: MenuSectionGeneric<SimpleMenuItemControlProps>) =>
      (section.controls[0] as WithSectionId<SimpleMenuItemControlProps>)
        ?.sectionId === groupId
  );

  // If we are not able to find a matching section in the menu definition, return undefined
  if (!section) {
    return undefined;
  }

  // Update the overflow menu definition to return
  const updatedOverflowMenuDefinition = {
    ...overflowMenuDefinition,
    sections: getGroupOverflowMenuDefinition(section, allOverflowControlIds),
  };

  return updatedOverflowMenuDefinition;
}

/**
 * Gets a correctly scoped menu definition for the control group overflow
 * @param section The menu section definition for this control group overflow
 * @param allOverflowControlIds An array of control id for all controls that are in overflows
 * @returns An updated menu definition for the groups overflow
 */
function getGroupOverflowMenuDefinition(
  section: MenuSectionGeneric<SimpleMenuItemControlProps>,
  allOverflowControlIds: string[]
) {
  const filteredSection: MenuSectionGeneric<SimpleMenuItemControlProps> = {
    ...section,
    controls: [],
  };
  filteredSection.controls = section.controls.filter(
    (control: SimpleMenuItemControlProps) =>
      allOverflowControlIds.some((controlId) => control.id === controlId)
  );
  return [filteredSection];
}

/**
 * Gets a correctly scoped menu definition for the single overflow
 * @param overflowMenuDefinition The single overflow menu definition
 * @param allOverflowControlIds An array of control id for all controls that are in overflows
 * @returns An updated menu definition for the single overflow
 */
function getSingleOverflowMenuDefinition(
  overflowMenuDefinition: MenuDefinition,
  allOverflowControlIds: string[]
) {
  if (!lastKnownScalingState) {
    return undefined;
  }

  const menuDefinitionForOverflow: MenuDefinition = { sections: [] };

  // Get the sections that using the single overflow
  lastKnownScalingState.ribbonGroupDefinitions.groups.map((group) => {
    if (!group.hasOwnOverflow || allOverflowControlIds.includes(group.id)) {
      const singleOverflowSection = overflowMenuDefinition.sections.find(
        (section: MenuSectionGeneric<SimpleMenuItemControlProps>) =>
          section.id === group.id
      );
      // Add the section, making sure we do not alter the original section definition
      singleOverflowSection &&
        menuDefinitionForOverflow.sections.push({ ...singleOverflowSection });
    }
  });

  // Iterate across the groups using single overflow and update the menu definition
  // consisting the sections/controls in each group's overflow
  menuDefinitionForOverflow.sections =
    menuDefinitionForOverflow.sections.filter(
      (section: MenuSectionGeneric<SimpleMenuItemControlProps>) => {
        const controlsForOverflow: SimpleMenuItemControlProps[] =
          section.controls.filter((control: SimpleMenuItemControlProps) =>
            allOverflowControlIds.find((controlId) => control.id === controlId)
          );

        if (controlsForOverflow.length > 0) {
          section.controls = controlsForOverflow;
          return true;
        }

        // If we have no controls for the overflow then return false to remove
        // the section from the menu definition
        return false;
      }
    );

  return menuDefinitionForOverflow;
}

/**
 * Adds the overflowing control ids for the given scaling step to the given overflowControlIds array
 * @param scalingStep The scaling step to build an array of controls that are overflowing
 * @param overflowControlIds The array to add the overflowing control ids
 */
function buildOverflowControlIdArray(
  scalingStep: Readonly<RibbonScalingStep> | Readonly<RibbonScalingStep>[],
  overflowControlIds: string[]
) {
  if (Array.isArray(scalingStep)) {
    scalingStep.map((step) =>
      buildOverflowControlIdArray(step, overflowControlIds)
    );
    return;
  }

  // Add the control or group id if the scaling step's type says that it is moved to an overflow
  if (stepContainsToOverflow(scalingStep as RibbonScalingTransformation)) {
    overflowControlIds.push(
      (scalingStep as MoveToOverflow).controlId ||
        (scalingStep as MoveGroupToSingleOverflow).groupId
    );
  }
}

/**
 * Determine the className for when the divider should be hidden (e.g. alongside the last control in the associated group that hides)
 * @param group The ribbon control group to find when its divider should be hidden
 * @param scalingSteps The current scaling steps
 * @returns Returns a function for getting the className that corresponds to the control group for when the divider should be hidden, or undefined if it won't be hidden
 */
export const getGroupDividerScalingStepClassName =
  (
    group: ScaledRibbonGroupSLR,
    scalingSteps: Readonly<RibbonScalingStep>[]
  ): (() => string | undefined) =>
  () => {
    // Keep track of when top level controls are hidden into the overflow
    const topLevelControlVisibilityMap: any = {};
    group.topLevelControls.map((control) => {
      topLevelControlVisibilityMap[control.id] = true; // visible
    });

    // Loop across the scaling steps to determine the step when the last top level control moves into the overflow.
    // This is the point at which the divider will be hidden. Make the className reference this scaling step
    for (
      let scalingStepIndex = 0;
      scalingStepIndex < scalingSteps.length;
      scalingStepIndex = scalingStepIndex + 1
    ) {
      const scalingStep = scalingSteps[scalingStepIndex];
      // Make sure we catch any move to overflows that are combined with other scaling steps
      if (Array.isArray(scalingStep)) {
        const toOverflowSteps = getToOverflowSteps(scalingStep);
        toOverflowSteps.forEach(
          (step: any) =>
            (topLevelControlVisibilityMap[step.controlId || step.groupId] =
              false)
        );
      } else {
        // We know this is a single scaling step and can mark the control if needed
        const singleStep = scalingStep as RibbonScalingTransformation;
        if (
          singleStep.type === "MoveToOverflow" ||
          singleStep.type === "MoveGroupToSingleOverflow"
        ) {
          topLevelControlVisibilityMap[(scalingStep as any).controlId] = false;
        }
      }

      // Check if we've gotten to the point where there are no
      // more top level controls. If so, we are done
      let hasTopLevelControls = false;
      for (const key in topLevelControlVisibilityMap) {
        if (topLevelControlVisibilityMap[key]) {
          hasTopLevelControls = true;
          break;
        }
      }

      if (!hasTopLevelControls) {
        return getMoveToOverflowScalingClassNameForScalingIndex(
          scalingStepIndex
        );
      }
    }

    return undefined;
  };
